import { INVENTORY_UPLOAD_ERROR_EVENT, INVENTORY_UPLOAD_ITEM_EVENT, RoomPrefix } from '@thinkalpha/locates-ws-client';
import type {
    InventoryUploadError,
    InventoryUploadErrorEvent,
    InventoryUploadItem,
    InventoryUploadItemEvent,
    JoinInventoryUploadRoomRequest,
} from '@thinkalpha/locates-ws-client';
import { reviver } from '@thinkalpha/ui-core/serializer';
import { io } from 'socket.io-client';
import type { Socket } from 'socket.io-client';
import { Decoder, Encoder } from 'socket.io-parser';
import { container } from 'src/StaticContainer';

// Resolved lazily so importing this module (e.g. in a unit test) doesn't
// require the DI container to be configured — only code paths that
// actually log touch it.
const getLog = () => container.get('Logger').getSubLogger({ name: 'InventoryUploadStream' }, { component: 'lab' });

// The push contract (room type, events, payloads) is published by
// `@thinkalpha/locates-ws-client` since 0.5.0; the local mirror this
// module used to carry is gone.
export type { InventoryUploadError, InventoryUploadItem } from '@thinkalpha/locates-ws-client';

export interface InventoryUploadStreamOptions {
    /** locates-ws socket URI (from `appConfig.locatesApi`). */
    uri: string;
    accessToken: string | undefined;
    /** Pool whose upload room to join. */
    poolId: string;
    /** Called once per distinct streamed item for the pool. */
    onItem: (item: InventoryUploadItem) => void;
    /**
     * Called once per distinct upload failure streamed for the pool
     * (`v1.inventory.pools.error`). The room is pool-scoped, so failures
     * from OTHER concurrent uploads arrive too — the caller correlates
     * on the payload's `uploadId` (the POST ack's id) and returns `true`
     * when the error settles ITS batch, which resolves any pending
     * {@link InventoryUploadStreamHandle.waitForItems} waiter rather
     * than sitting out an idle window waiting for items that will never
     * arrive.
     */
    onError?: (error: InventoryUploadError) => boolean | undefined;
    /**
     * Test seam: supply a socket instead of opening a real one. Defaults
     * to a websocket connected to {@link InventoryUploadStreamOptions.uri}.
     */
    socketFactory?: (uri: string) => Socket;
}

export interface InventoryUploadStreamHandle extends Disposable {
    /**
     * Resolve `true` once the socket has authenticated and joined the
     * pool's upload room, or `false` if authentication fails, the stream
     * is disposed, or `timeoutMs` elapses first. Lets the caller skip
     * {@link waitForItems} and fall back to a non-streamed progress path
     * instead of blocking on item events that will never arrive when the
     * socket never becomes usable (missing/rejected token, no connection).
     * Resolves immediately once the ready state has settled.
     */
    whenReady(timeoutMs: number): Promise<boolean>;
    /**
     * Resolve once `expected` distinct items for the pool have streamed
     * in (`'reached'`). Falls back to resolving after `idleTimeoutMs`
     * elapses with no new item (`'idle'`) — a safety net for a dropped
     * event or an `expected` the stream never reaches, without cutting
     * off an actively-arriving batch — or when a claimed error settles
     * the batch (`'error'`). Resolves immediately when `expected <= 0` or already
     * satisfied. The idle timer resets on every new item.
     */
    waitForItems(expected: number, idleTimeoutMs: number): Promise<'reached' | 'idle' | 'error'>;
    /**
     * Resolve when a claimed error arrives (see
     * {@link InventoryUploadStreamOptions.onError}), or after `graceMs`,
     * or on dispose — whichever comes first. Lets a caller whose
     * {@link waitForItems} idled out short of its expectation hold the
     * listener open briefly for a trailing terminal error instead of
     * tearing it down mid-arrival.
     */
    waitForError(graceMs: number): Promise<void>;
}

function defaultSocketFactory(uri: string): Socket {
    // Mirrors `createLocatesSocket` (store/channels/util) but without the
    // redux-saga imports that module pulls in.
    return io(uri, {
        timeout: 10000,
        transports: ['websocket'],
        parser: {
            Encoder,
            Decoder: class extends Decoder {
                constructor() {
                    super(reviver);
                }
            },
        },
        reconnectionDelay: 500,
        reconnectionDelayMax: 60000,
        randomizationFactor: 0.1,
    });
}

/**
 * Subscribe to the live inventory-upload item stream for a pool. The
 * caller drives progress from {@link InventoryUploadStreamOptions.onItem}
 * and awaits {@link InventoryUploadStreamHandle.waitForItems} to know
 * when the backend has finished emitting a batch.
 */
export function openInventoryUploadStream(opts: InventoryUploadStreamOptions): InventoryUploadStreamHandle {
    const socket = (opts.socketFactory ?? defaultSocketFactory)(opts.uri);
    let disposed = false;
    // Distinct item ids seen for this pool, so a re-emit can't double-count.
    // Keyed by item id rather than uploadId: the room is already scoped to
    // this pool, and the streamed items' uploadId does not reliably match
    // the POST ack's uploadId, so filtering on it drops every event.
    const seen = new Set<string>();
    // Each waiter resolves when the count is reached; `bumpIdle` restarts
    // its idle timer so an actively-streaming batch is never cut short.
    const waiters: Array<{
        expected: number;
        resolve: (reason: 'reached' | 'idle' | 'error') => void;
        bumpIdle: () => void;
    }> = [];
    // Callers holding the listener open for a trailing claimed error.
    const errorWaiters: Array<() => void> = [];
    let claimedErrorSeen = false;

    // Ready = authenticated + joined the pool room. `null` while pending,
    // then latched to the settled result so `whenReady` can answer late
    // callers without re-waiting.
    let ready: boolean | null = null;
    const readyWaiters: Array<(ok: boolean) => void> = [];
    const settleReady = (ok: boolean): void => {
        if (ready !== null) return;
        ready = ok;
        for (const resolve of readyWaiters.splice(0)) resolve(ok);
    };

    const onItemProgress = (): void => {
        for (let i = waiters.length - 1; i >= 0; i -= 1) {
            const w = waiters[i]!;
            if (seen.size >= w.expected) {
                waiters.splice(i, 1);
                w.resolve('reached');
            } else {
                w.bumpIdle();
            }
        }
    };

    socket.on('connect', () => {
        if (TRACE) {
            getLog().trace('Socket connected, authenticating');
        }
        socket.emit('auth', opts.accessToken);
    });

    socket.on('auth.success', () => {
        if (TRACE) {
            getLog().trace('Authenticated, joining inventory upload room', { poolId: opts.poolId });
        }
        // A fresh socket has no prior membership to clear, and the
        // published contract's `room.leave` requires a room name — the
        // bare emit this used to send was a no-op.
        const join = { type: RoomPrefix.InventoryUpload, poolId: opts.poolId } satisfies JoinInventoryUploadRoomRequest;
        socket.emit('room.join', join);
        settleReady(true);
    });

    socket.on('auth.error', (error: unknown) => {
        getLog().error({
            message: 'Inventory upload stream authentication failed',
            error: error instanceof Error ? error : new Error(String(error)),
        });
        settleReady(false);
    });

    socket.on('connect_error', (error: unknown) => {
        getLog().error({
            message: 'Inventory upload stream connection failed',
            error: error instanceof Error ? error : new Error(String(error)),
        });
    });

    socket.on(INVENTORY_UPLOAD_ITEM_EVENT, (payload: InventoryUploadItemEvent) => {
        if (disposed) return;
        const item = payload?.item;
        if (!item || item.poolId !== opts.poolId) return;
        if (seen.has(item.id)) return;
        seen.add(item.id);
        opts.onItem(item);
        onItemProgress();
    });

    // Distinct streamed failures, keyed like the items so a re-emit can't
    // report the same failure twice.
    const seenErrors = new Set<string>();
    socket.on(INVENTORY_UPLOAD_ERROR_EVENT, (payload: InventoryUploadErrorEvent) => {
        if (disposed) return;
        const error = payload?.error;
        if (!error || error.poolId !== opts.poolId) return;
        const key = `${error.uploadId}:${error.reason}`;
        if (seenErrors.has(key)) return;
        seenErrors.add(key);
        // Only an error the caller claims as its own settles the batch:
        // rows that failed will never stream, so waiting for the expected
        // count would only burn the idle window. A foreign upload's error
        // must not cut this batch's wait short.
        if (opts.onError?.(error) === true) {
            claimedErrorSeen = true;
            for (const w of waiters.splice(0)) w.resolve('error');
            for (const resolve of errorWaiters.splice(0)) resolve();
        }
    });

    if (!socket.active) {
        socket.connect();
    }

    return {
        whenReady(timeoutMs) {
            if (ready !== null) return Promise.resolve(ready);
            return new Promise<boolean>((resolve) => {
                let settled = false;
                const timer = setTimeout(() => {
                    if (settled) return;
                    settled = true;
                    resolve(false);
                }, timeoutMs);
                // Left in `readyWaiters` after a timeout but guarded by
                // `settled`; dispose/settle clears the array either way.
                const cb = (ok: boolean): void => {
                    if (settled) return;
                    settled = true;
                    clearTimeout(timer);
                    resolve(ok);
                };
                readyWaiters.push(cb);
            });
        },
        waitForItems(expected, idleTimeoutMs) {
            if (expected <= 0 || seen.size >= expected) return Promise.resolve('reached' as const);
            return new Promise<'reached' | 'idle' | 'error'>((resolve) => {
                let timer: ReturnType<typeof setTimeout>;
                const finish = (reason: 'reached' | 'idle' | 'error'): void => {
                    clearTimeout(timer);
                    const idx = waiters.indexOf(waiter);
                    if (idx >= 0) waiters.splice(idx, 1);
                    resolve(reason);
                };
                // (Re)arm the idle timer — fires only after a full quiet
                // window with no new item.
                const bumpIdle = (): void => {
                    clearTimeout(timer);
                    timer = setTimeout(() => finish('idle'), idleTimeoutMs);
                };
                const waiter = { expected, resolve: finish, bumpIdle };
                waiters.push(waiter);
                bumpIdle();
            });
        },
        waitForError(graceMs) {
            if (claimedErrorSeen || disposed) return Promise.resolve();
            return new Promise<void>((resolve) => {
                let settled = false;
                const done = (): void => {
                    if (settled) return;
                    settled = true;
                    clearTimeout(timer);
                    // Drop the settled waiter so repeated grace waits on a
                    // long-lived stream don't accumulate dead closures.
                    const index = errorWaiters.indexOf(done);
                    if (index >= 0) errorWaiters.splice(index, 1);
                    resolve();
                };
                const timer = setTimeout(done, graceMs);
                errorWaiters.push(done);
            });
        },
        [Symbol.dispose]() {
            if (disposed) return;
            disposed = true;
            // Unblock a caller still awaiting readiness — a disposed stream
            // will never join, so report it as not ready.
            settleReady(false);
            for (const w of waiters.splice(0)) w.resolve('idle');
            for (const resolve of errorWaiters.splice(0)) resolve();
            socket.removeAllListeners();
            // `room.leave` needs an established connection, but `disconnect()`
            // must run unconditionally — otherwise disposing mid-handshake
            // leaves the pending connection (and its reconnection attempts)
            // alive after the caller believes the stream is torn down.
            // The published contract's `room.leave` requires a room name
            // and has no "leave everything" form; disconnecting drops the
            // membership server-side.
            socket.disconnect();
        },
    };
}
